import { useEffect, useRef, type ReactNode } from "react";
import { Group, Segmented, Slider } from "./controls.tsx";
import {
  TEXT_FONTS,
  TEXT_LOOKS,
  TEXT_SWATCHES,
  loadFont,
  type TextAlign,
  type TextFont,
  type TextStyle,
  type TextValign,
} from "./text.ts";
import { setTextContent, setTextStyle, setTransform, type Clip, type Project } from "./timeline.ts";

/**
 * The words and how they look.
 *
 * Ordered by how often each thing gets touched: the words first, then a row of
 * whole looks that get most people all the way there, then the individual
 * controls for going on from a look. Where a title *sits* is the layout
 * panel's business, the same as for a picture.
 */
export function TextPanel({
  project,
  clip,
  focus,
  onChange,
}: {
  project: Project;
  clip: Clip;
  /** Put the caret in the words: set for a title that has just been made. */
  focus: boolean;
  onChange: (project: Project) => void;
}) {
  const text = clip.text;
  const area = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (focus) {
      area.current?.focus();
      area.current?.select();
    }
  }, [focus, clip.id]);
  if (!text) return null;
  const { style } = text;
  const set = (patch: Partial<TextStyle>) => {
    const next = setTextStyle(project, clip.id, patch);
    // Fetched now so the preview is right by the time the next frame paints.
    void loadFont({ ...style, ...patch });
    onChange(next);
  };
  const face = TEXT_FONTS[style.font];
  const sizePx = Math.round(style.size * Math.min(project.width, project.height));
  const current = TEXT_LOOKS.find((look) => sameLook(look.style, style))?.id ?? null;

  return (
    <div className="flex flex-col gap-3.5">
      <textarea
        ref={area}
        value={text.content}
        rows={Math.min(6, Math.max(2, text.content.split("\n").length + 1))}
        aria-label="The words"
        placeholder="Type your text"
        onChange={(event) => onChange(setTextContent(project, clip.id, event.target.value))}
        className="w-full resize-y rounded-[8px] border border-rule bg-[#fbfaf5] px-2.5 py-2 font-sans text-[14px] leading-snug text-ink outline-none transition-colors focus:border-pine"
      />

      <Group label="Look" first>
        <div role="group" aria-label="Ready-made looks" className="grid grid-cols-5 gap-1">
          {TEXT_LOOKS.map((look) => (
            <button
              key={look.id}
              type="button"
              aria-pressed={current === look.id}
              title={look.label}
              // The size stays: a look is about colour and shape, and a title
              // someone has already sized should not jump when they try one.
              onClick={() => set({ ...look.style, size: style.size, align: style.align, valign: style.valign })}
              className={`flex flex-col items-center gap-1 rounded-[7px] border px-1 py-1.5 transition-colors ${
                current === look.id
                  ? "border-pine-deep bg-pine/10"
                  : "border-rule bg-cream-2/40 hover:border-pine hover:bg-cream-2"
              }`}
            >
              <LookSwatch style={look.style} />
              <span className="text-[9.5px] font-medium text-pine">{look.label}</span>
            </button>
          ))}
        </div>
      </Group>

      <Group label="Type">
        <Segmented<TextFont>
          label="Font"
          options={(Object.keys(TEXT_FONTS) as TextFont[]).map((font) => ({
            value: font,
            label: TEXT_FONTS[font].label,
            hint: TEXT_FONTS[font].family.split(",")[0]?.replace(/"/g, "") ?? font,
          }))}
          selected={style.font}
          onSelect={(font) => set({ font })}
        />
        <div className="flex gap-1">
          <Toggle
            label="Bold"
            pressed={style.bold}
            disabled={face.regular === face.bold}
            onClick={() => set({ bold: !style.bold })}
          >
            <span className="font-bold">B</span>
          </Toggle>
          <Toggle label="Italic" pressed={style.italic} onClick={() => set({ italic: !style.italic })}>
            <span className="italic">I</span>
          </Toggle>
          <span className="w-2" />
          <div className="min-w-0 flex-1">
            <Segmented<TextAlign>
              label="Alignment"
              options={[
                { value: "left", label: "Left", hint: "Lines start at the left of the box" },
                { value: "center", label: "Centre", hint: "Lines centred in the box" },
                { value: "right", label: "Right", hint: "Lines end at the right of the box" },
              ]}
              selected={style.align}
              onSelect={(align) => set({ align })}
            />
          </div>
        </div>
        {clip.transform.height !== undefined ? (
          <Segmented<TextValign>
            label="Vertical alignment"
            options={[
              { value: "top", label: "Top", hint: "Words at the top of the box" },
              { value: "middle", label: "Middle", hint: "Words in the middle of the box" },
              { value: "bottom", label: "Bottom", hint: "Words at the bottom of the box" },
            ]}
            selected={style.valign}
            onSelect={(valign) => set({ valign })}
          />
        ) : null}
        <Slider
          label="Size"
          value={style.size}
          min={0.02}
          max={0.3}
          step={0.005}
          format={() => `${sizePx} px`}
          onChange={(size) => set({ size })}
        />
        <Slider
          label="Line spacing"
          value={style.lineHeight}
          min={0.8}
          max={2}
          step={0.05}
          format={(value) => `${value.toFixed(2)}×`}
          onChange={(lineHeight) => set({ lineHeight })}
        />
        <ColorRow label="Colour" value={style.color} onChange={(color) => set({ color })} />
      </Group>

      <Group label="Background">
        <Segmented<"none" | "box">
          label="Background"
          options={[
            { value: "none", label: "None", hint: "The words alone" },
            { value: "box", label: "Box", hint: "A filled box behind the words" },
          ]}
          selected={style.background ? "box" : "none"}
          onSelect={(choice) =>
            set({
              background:
                choice === "none" ? null : (style.background ?? { color: "#14100c", opacity: 0.75, padding: 0.4 }),
            })
          }
        />
        {style.background ? (
          <>
            <ColorRow
              label="Box colour"
              value={style.background.color}
              onChange={(color) => set({ background: { ...style.background!, color } })}
            />
            <Slider
              label="Box opacity"
              value={style.background.opacity}
              min={0}
              max={1}
              step={0.05}
              format={(value) => `${Math.round(value * 100)}%`}
              onChange={(opacity) => set({ background: { ...style.background!, opacity } })}
            />
            <Slider
              label="Padding"
              value={style.background.padding}
              min={0}
              max={1.5}
              step={0.05}
              format={(value) => `${Math.round(value * 100)}%`}
              onChange={(padding) => set({ background: { ...style.background!, padding } })}
            />
            <Slider
              label="Corner rounding"
              value={clip.transform.radius}
              min={0}
              max={0.5}
              step={0.01}
              format={(value) => (value >= 0.5 ? "pill" : `${Math.round(value * 200)}%`)}
              onChange={(radius) => onChange(setTransform(project, clip.id, { radius }))}
            />
          </>
        ) : null}
      </Group>

      <Group label="Edge">
        <Segmented<"none" | "thin" | "thick">
          label="Outline"
          options={[
            { value: "none", label: "No outline", hint: "Plain letters" },
            { value: "thin", label: "Thin", hint: "A fine line around each letter" },
            { value: "thick", label: "Thick", hint: "The heavy subtitle outline" },
          ]}
          selected={!style.outline ? "none" : style.outline.width > 0.08 ? "thick" : "thin"}
          onSelect={(choice) =>
            set({
              outline:
                choice === "none"
                  ? null
                  : { color: style.outline?.color ?? "#000000", width: choice === "thick" ? 0.12 : 0.05 },
            })
          }
        />
        {style.outline ? (
          <ColorRow
            label="Outline colour"
            value={style.outline.color}
            onChange={(color) => set({ outline: { ...style.outline!, color } })}
          />
        ) : null}
        <label className="flex items-center justify-between gap-2 text-[12px] text-soft">
          Soft shadow
          <input
            type="checkbox"
            checked={style.shadow}
            onChange={(event) => set({ shadow: event.target.checked })}
            className="accent-pine"
          />
        </label>
      </Group>
    </div>
  );
}

/** Whether a style is one of the looks, ignoring the things a look leaves alone. */
function sameLook(look: TextStyle, style: TextStyle): boolean {
  const strip = ({ size: _s, align: _a, valign: _v, ...rest }: TextStyle) => JSON.stringify(rest);
  return strip(look) === strip(style);
}

/** A tiny "Aa" in the look's own colours, so the row can be read without its labels. */
function LookSwatch({ style }: { style: TextStyle }) {
  const face = TEXT_FONTS[style.font];
  return (
    <span
      aria-hidden="true"
      className="flex h-7 w-full items-center justify-center rounded-[5px] text-[13px] leading-none"
      style={{
        background: style.background
          ? withOpacity(style.background.color, style.background.opacity)
          : "linear-gradient(135deg, #4f8496, #2d554e)",
        color: style.color,
        fontFamily: face.family,
        fontWeight: style.bold ? face.bold : face.regular,
        fontStyle: style.italic ? "italic" : "normal",
        WebkitTextStroke: style.outline ? `0.6px ${style.outline.color}` : undefined,
        textShadow: style.shadow ? "0 1px 2px rgba(0,0,0,0.6)" : undefined,
      }}
    >
      Aa
    </span>
  );
}

function withOpacity(hex: string, opacity: number): string {
  const match = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex);
  if (!match) return hex;
  const [r, g, b] = [match[1], match[2], match[3]].map((part) => parseInt(part ?? "0", 16));
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

function Toggle({
  label,
  pressed,
  disabled,
  onClick,
  children,
}: {
  label: string;
  pressed: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={disabled ? `${label} is the only weight this face has` : label}
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      className={`flex size-8 shrink-0 items-center justify-center rounded-[8px] border text-[13px] transition-colors disabled:opacity-40 ${
        pressed ? "border-pine-deep bg-pine text-cream" : "border-rule bg-cream-2/40 text-pine hover:bg-cream-2"
      }`}
    >
      {children}
    </button>
  );
}

/** Six swatches and a picker. The swatches are the palette; the picker is everything else. */
function ColorRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (color: string) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-[12px] text-soft">{label}</span>
      <span role="group" aria-label={label} className="flex items-center gap-1">
        {TEXT_SWATCHES.map((swatch) => (
          <button
            key={swatch}
            type="button"
            aria-label={`${label} ${swatch}`}
            aria-pressed={value.toLowerCase() === swatch}
            onClick={() => onChange(swatch)}
            className={`size-5 rounded-full border transition-transform hover:scale-110 ${
              value.toLowerCase() === swatch ? "border-pine-deep ring-2 ring-pine/40" : "border-rule"
            }`}
            style={{ background: swatch }}
          />
        ))}
        <label
          className="relative ml-1 flex size-5 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-rule"
          title={`${label}: pick any colour`}
          style={{ background: "conic-gradient(#f25f5c, #ffd23f, #a6b58f, #4f8496, #2d554e, #f25f5c)" }}
        >
          <input
            type="color"
            aria-label={`${label}, any colour`}
            value={value}
            onChange={(event) => onChange(event.target.value)}
            className="absolute inset-0 size-full cursor-pointer opacity-0"
          />
        </label>
      </span>
    </div>
  );
}
